/**
 * useChallenge Hook
 * =================
 * State machine for liveness detection challenges.
 * Manages challenge progression, timing, and completion.
 */

import { useState, useRef, useCallback } from 'react';

const CENTER_CHALLENGE = {
  id: 'center',
  label: 'Center your face',
  instruction: 'Position your face in the oval',
  holdDuration: 500,
};

const TURN_CHALLENGES = [
  {
    id: 'turn_left',
    label: 'Turn left',
    instruction: 'Turn your head to the left',
    holdDuration: 200,
  },
  {
    id: 'turn_right',
    label: 'Turn right',
    instruction: 'Turn your head to the right',
    holdDuration: 200,
  },
];

// Generate randomized challenge sequence: center first, then shuffled turns
function generateChallenges() {
  const shuffledTurns = [...TURN_CHALLENGES].sort(() => Math.random() - 0.5);
  return [CENTER_CHALLENGE, ...shuffledTurns];
}

// Global timeout for all challenges
const GLOBAL_TIMEOUT_MS = 30000;

export function useChallenge() {
  const [challenges, setChallenges] = useState(() => generateChallenges());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [status, setStatus] = useState('idle'); // idle, running, passed, failed, timeout
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const [holdProgress, setHoldProgress] = useState(0); // 0-100 for current hold
  const globalTimeoutRef = useRef(null);
  const holdStartRef = useRef(null);
  const graceCountRef = useRef(0); // Allow brief flickers without resetting hold
  const GRACE_FRAMES = 4;

  const currentChallenge = challenges[currentIndex] || null;
  const progress = completedChallenges.length / challenges.length;

  /**
   * Start the challenge sequence.
   */
  const start = useCallback(() => {
    // Generate fresh random challenge order each session
    setChallenges(generateChallenges());
    setStatus('running');
    setCurrentIndex(0);
    setCompletedChallenges([]);
    setHoldProgress(0);
    holdStartRef.current = null;
    graceCountRef.current = 0;

    // Global timeout
    globalTimeoutRef.current = setTimeout(() => {
      setStatus('timeout');
    }, GLOBAL_TIMEOUT_MS);
  }, []);

  /**
   * Called each frame when the challenge condition IS met.
   * Handles hold duration if required.
   * @returns {boolean} True if this frame caused the challenge to pass
   */
  const onConditionMet = useCallback(() => {
    if (status !== 'running') return false;
    graceCountRef.current = 0; // Reset grace counter on good frame

    const challenge = challenges[currentIndex];
    if (!challenge) return false;

    // No hold required - instant pass
    if (challenge.holdDuration === 0) {
      const nextIndex = currentIndex + 1;
      setCompletedChallenges(prev => [...prev, challenge.id]);
      setHoldProgress(0);
      holdStartRef.current = null;

      if (nextIndex >= challenges.length) {
        setStatus('passed');
        clearTimeout(globalTimeoutRef.current);
      } else {
        setCurrentIndex(nextIndex);
      }
      return true;
    }

    // Hold required - track duration
    const now = Date.now();
    if (holdStartRef.current === null) {
      holdStartRef.current = now;
    }

    const elapsed = now - holdStartRef.current;
    const progressPct = Math.min(100, (elapsed / challenge.holdDuration) * 100);
    setHoldProgress(progressPct);

    if (elapsed >= challenge.holdDuration) {
      // Hold complete - pass challenge
      const nextIndex = currentIndex + 1;
      setCompletedChallenges(prev => [...prev, challenge.id]);
      setHoldProgress(0);
      holdStartRef.current = null;

      if (nextIndex >= challenges.length) {
        setStatus('passed');
        clearTimeout(globalTimeoutRef.current);
      } else {
        setCurrentIndex(nextIndex);
      }
      return true;
    }

    return false;
  }, [status, currentIndex, challenges]);

  /**
   * Called each frame when the challenge condition is NOT met.
   * Allows a grace period of GRACE_FRAMES before resetting hold progress.
   */
  const onConditionNotMet = useCallback(() => {
    graceCountRef.current++;
    if (graceCountRef.current > GRACE_FRAMES && holdStartRef.current !== null) {
      holdStartRef.current = null;
      setHoldProgress(0);
    }
  }, []);

  /**
   * Reset the challenge state machine.
   */
  const reset = useCallback(() => {
    clearTimeout(globalTimeoutRef.current);
    setChallenges(generateChallenges()); // Fresh random order
    setCurrentIndex(0);
    setStatus('idle');
    setCompletedChallenges([]);
    setHoldProgress(0);
    holdStartRef.current = null;
    graceCountRef.current = 0;
  }, []);

  return {
    challenges,
    currentChallenge,
    currentIndex,
    status,
    completedChallenges,
    progress,
    holdProgress,
    start,
    onConditionMet,
    onConditionNotMet,
    reset,
  };
}
